import React, { useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Tooltip from '@mui/material/Tooltip';
import { SensorLatest } from '../../services/api';
import { getActiveMarker, getSensorsById } from '../../state/selectors';
import { getBandColor, SensorFilteredStats, PlotType } from './helpers';
import useGlobalStyles from '../../styles';
import useStyles from '../../styles/calendarView';
import { setSelectedSensors } from '../../state/actions';
import ReportsAddCircleIcon from '../../styles/icons/ReportsAddCircleIcon';
import { useAppDispatch, useAppSelector } from '../../state/store';
import { themeProps } from '../../styles/theme';
import { TagSummaryRef } from './CalendarViewDataContainer';
import ValuePill from './ValuePill';
import useLocAllSpaces from '../Map/useLocAllSpaces';
import TagListItem from './TagListItem';
import { SpaceParams } from '../Map/mapHelpers';

interface TagViewSummaryProps {
  sensorFilteredStats: Map<string, SensorFilteredStats>;
  valuesRange: [number, number];
  summaryType: PlotType;
}

const TagViewSummary = forwardRef<TagSummaryRef, TagViewSummaryProps>(
  ({ sensorFilteredStats, valuesRange, summaryType }, ref): JSX.Element => {
    const { classes: globalClasses } = useGlobalStyles();
    const { classes } = useStyles();
    const dispatch = useAppDispatch();
    const sensorsById = useAppSelector(getSensorsById);
    const activeMarker = useAppSelector(getActiveMarker);
    const [selectedSpaces, setSelectedSpaces] = useState<string[]>();
    const [showHistogram, setShowHistogram] = useState(true);
    const allSpaces = useLocAllSpaces();

    const allTaggedSensors = Array.from(new Set(allSpaces.flatMap((item) => item.sensors)));

    useImperativeHandle(
      ref,
      () => ({
        clearState: () => {
          setSelectedSpaces([]);
        },
      }),
      []
    );

    // avoid clearing sensors selected from compare page
    useEffect(() => {
      if (selectedSpaces !== undefined) {
        const spaceSensors: string[] = [];
        selectedSpaces.forEach((spaceId) => {
          const spaceDetail = allSpaces.find((item) => item.title === spaceId);
          if (spaceDetail) spaceSensors.push(...spaceDetail.sensors);
        });
        dispatch(setSelectedSensors(spaceSensors));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSpaces]);

    const sensors = useMemo(
      () => allTaggedSensors.map((id) => sensorsById.get(id) ?? ({ id } as SensorLatest)),
      [allTaggedSensors, sensorsById]
    );

    const minMaxValue = useMemo(() => {
      let minValue: number | undefined;
      let maxValue: number | undefined;
      sensors.forEach((sensor) => {
        const min = sensorFilteredStats.get(sensor.id)?.stats.maxMinAvg.min;
        const max = sensorFilteredStats.get(sensor.id)?.stats.maxMinAvg.max;
        if (min !== undefined && !Number.isNaN(min)) {
          if (minValue === undefined) minValue = min;
          else if (min < minValue) minValue = min;
        }
        if (max !== undefined && !Number.isNaN(max)) {
          if (maxValue === undefined) maxValue = max;
          else if (max > maxValue) maxValue = max;
        }
      });
      return { minValue, maxValue };
    }, [sensorFilteredStats, sensors]);

    function getAverageSummaryValue(space: SpaceParams): number | undefined {
      const summaryValues = space.sensors.map(
        (sensorId: string) => sensorFilteredStats.get(sensorId)?.summaryValue
      );

      const filteredValues = summaryValues.filter(
        (value): value is number => value !== null && value !== undefined && !Number.isNaN(value)
      );

      const average =
        filteredValues.length > 0
          ? filteredValues.reduce((sum: number, value: number) => sum + value, 0) /
            filteredValues.length
          : undefined;

      return Number(average?.toFixed(1));
    }

    return (
      <div>
        <List>
          <ListItem sx={{ padding: '8px' }}>
            <Grid container spacing={1}>
              <Grid size={{ md: 1, sm: 1, xs: 2 }} className={classes.centered}>
                <Tooltip title="Add to compare" placement="top">
                  <div>
                    <ReportsAddCircleIcon />
                  </div>
                </Tooltip>
              </Grid>

              <Grid
                size={{ md: 2, sm: 2, xs: 2 }}
                sx={{ alignSelf: 'center' }}
                className={globalClasses.hideInSmallScreen}
              >
                <Typography variant="h6">Name</Typography>
              </Grid>
              <Grid
                size={{ md: 1, sm: 1 }}
                className={`${classes.centered} ${globalClasses.hideInSmallScreen}`}
              >
                <Typography variant="h6">{summaryType}</Typography>
              </Grid>
              <Grid
                size={{ md: 4, sm: 4, xs: 8 }}
                className={`${classes.centered} ${globalClasses.centeredFlex}`}
              >
                {minMaxValue.maxValue && <ValuePill value={minMaxValue.minValue} />}
                <Button
                  onClick={() => setShowHistogram(!showHistogram)}
                  variant={themeProps.btnVariant.default}
                >
                  {showHistogram ? 'Histogram' : 'Inline'}
                </Button>
                {minMaxValue.maxValue && <ValuePill value={minMaxValue.maxValue} />}
              </Grid>

              <List sx={{ width: '100%' }}>
                {allSpaces.map((space) => {
                  const summaryValue = getAverageSummaryValue(space);
                  const color = !Number.isNaN(summaryValue)
                    ? getBandColor(getAverageSummaryValue(space), activeMarker)
                    : themeProps.colors.lightGrey;

                  return (
                    <Box key={space.title} sx={{ width: '100%' }}>
                      <TagListItem
                        space={space}
                        allSpaces={allSpaces}
                        summaryValue={summaryValue}
                        color={color}
                        valuesRange={valuesRange}
                        selectedSpaces={selectedSpaces ?? []}
                        setSelectedSpaces={setSelectedSpaces}
                        sensorFilteredStats={sensorFilteredStats}
                        showHistogram={showHistogram}
                      />
                    </Box>
                  );
                })}
              </List>
            </Grid>
          </ListItem>
        </List>
      </div>
    );
  }
);

export default TagViewSummary;
